/**
 * 공종 — **누가 하나**. BASIC 10 + STYLE 5 가 내장 기본 코드다.
 *
 * 여기 있는 15개는 이제 「값의 전부」 가 아니라 **내장 기본 코드**다 — 고객 코드값
 * (`modules/org/codes`)과 같은 패턴이다. 조직이 아무것도 고치지 않았을 때 쓰는
 * 목록이자, 지우지 못하는 바닥이다(비활성만 된다). 실제로 고를 수 있는 값은
 * 저장소의 `trade` 행이 정한다.
 *
 * 금액 집계는 그룹별로 나뉘므로(subtotal_basic·subtotal_style) 그룹이 코드에 붙어 있다.
 *
 * 공정(언제 하나)은 다른 축이라 `phases.ts` 에 따로 있다. 여기 `sort_order` 는
 * **견적서 목차 순서**이지 시공 순서가 아니다 — 도배는 목차에서 9번이지만 시공은
 * 거의 끝이다.
 */

import { BaseRow } from "../common/types";
import { PhaseCode } from "./phases";

export const TRADE_GROUPS = ["BASIC", "STYLE"] as const;
export type TradeGroup = (typeof TRADE_GROUPS)[number];

export const TRADE_GROUP_LABEL: Record<TradeGroup, string> = {
  BASIC: "기본 공사",
  STYLE: "스타일 공사",
};

export interface TradeDef {
  code: BuiltinTradeCode;
  group: TradeGroup;
  name: string;
  sort_order: number;
}

/**
 * 내장 기본 공종 코드. **값의 전부가 아니다** — 회사가 추가한 코드가 견적에
 * 들어올 수 있다. 무엇을 고를 수 있는지는 저장소의 `trade` 행이 정한다.
 */
export const TRADE_CODES = [
  "docs",
  "demolition",
  "plumbing",
  "carpentry",
  "electric",
  "tile",
  "film",
  "paint",
  "wallpaper",
  "finish",
  "window",
  "floor",
  "door",
  "bath",
  "furniture",
] as const;
/** 내장 코드만. 내장 표(`TRADE_LABEL` 등)를 색인할 때 쓴다. */
export type BuiltinTradeCode = (typeof TRADE_CODES)[number];

/**
 * 견적·단가표에 **실제로 저장되는 값**. 회사가 추가한 코드가 들어오므로 열려 있다.
 *
 * 내장 표를 이 타입으로 색인하지 않는다 — 열어 두면 모르는 코드로 읽어도 타입이
 * `string` 이라 `undefined` 가 화면까지 그대로 나간다. 표기는 `resolveTradeName`
 * 이 붙이고, 그 자리에서 모르는 코드를 다룬다.
 */
export type TradeCode = string;

const define = (
  code: BuiltinTradeCode,
  group: TradeGroup,
  name: string,
  sort_order: number
): TradeDef => ({ code, group, name, sort_order });

export const TRADES: readonly TradeDef[] = [
  define("docs", "BASIC", "서류대행 / 보양", 1),
  define("demolition", "BASIC", "철거 공사", 2),
  define("plumbing", "BASIC", "설비 공사", 3),
  define("carpentry", "BASIC", "목공 공사", 4),
  define("electric", "BASIC", "전기설비 / 조명", 5),
  define("tile", "BASIC", "타일 공사", 6),
  define("film", "BASIC", "필름 공사", 7),
  define("paint", "BASIC", "도장 공사", 8),
  define("wallpaper", "BASIC", "도배 공사", 9),
  define("finish", "BASIC", "마감 공사", 10),
  define("window", "STYLE", "창호 공사", 11),
  define("floor", "STYLE", "마루 공사", 12),
  define("door", "STYLE", "도어 공사", 13),
  define("bath", "STYLE", "바스 공사", 14),
  define("furniture", "STYLE", "가구 공사", 15),
];

/**
 * 내장 공종의 **기본 공정**. 견적을 만들 때 공종이 이 값을 복사해 간다.
 *
 * 복사인 이유 — 도배·마루의 선후가 현장마다 갈리므로 견적의 공종이 자기
 * `phase_id` 를 들고 덮어쓸 수 있어야 한다. 여기를 바라보게 하면 한 현장의
 * 순서를 바꿨을 때 지난 견적까지 같이 흔들린다.
 */
export const BUILTIN_TRADE_PHASE: Record<BuiltinTradeCode, PhaseCode> = {
  docs: "prep",
  demolition: "demolition",
  plumbing: "plumbing",
  electric: "plumbing",
  carpentry: "frame",
  window: "frame",
  tile: "wet",
  paint: "dry",
  film: "dry",
  wallpaper: "dry",
  floor: "floor",
  door: "install",
  bath: "install",
  furniture: "install",
  finish: "finish",
};

/**
 * 공종 한 줄.
 *
 * `org_id` 가 **null 이면 빌드파이 표준**, 값이 있으면 그 회사가 추가한 것이다.
 * `code` 는 **불변**이다 — 단가표의 자연키가 `(org_id, trade, scope, item, unit)`
 * 라서 코드를 바꾸면 그 공종의 단가 이력이 통째로 끊긴다. 이름만 고친다.
 */
export interface Trade extends Omit<BaseRow, "id"> {
  id: string;
  /** null = 빌드파이 표준. */
  org_id: string | null;
  /** 불변. */
  code: string;
  /** 회사가 고칠 수 있다. 고쳐도 지난 견적의 표기는 그대로다(발송 시 굳는다). */
  name: string;
  group: TradeGroup;
  /** 기본 공정. 견적의 공종이 복사해 간 뒤 각자 덮어쓴다. */
  phase_id: string | null;
  /** false 면 숨김. 표준 행은 지우지 못한다. */
  active: boolean;
  /** 견적서 목차 순서. */
  sort_order: number;
}

/** 표준 행의 id. 코드에서 바로 만든다 — `builtinPhaseId` 와 같은 이유. */
export const builtinTradeId = (code: string): string => `trd_${code}`;

const byCode = new Map<BuiltinTradeCode, TradeDef>(
  TRADES.map((t) => [t.code, t])
);

export const TRADE_LABEL = Object.fromEntries(
  TRADES.map((t) => [t.code, t.name])
) as Record<BuiltinTradeCode, string>;

export const getTrade = (code: string): TradeDef => {
  const def = byCode.get(code as BuiltinTradeCode);
  if (!def) throw new Error(`알 수 없는 공종 코드: ${code}`);
  return def;
};

/** 내장 코드인가. 회사가 추가한 코드는 false 다. */
export const isBuiltinTradeCode = (
  value: unknown
): value is BuiltinTradeCode =>
  typeof value === "string" && byCode.has(value as BuiltinTradeCode);

/**
 * 화면에 붙일 공종 이름. 저장소 목록이 먼저고, 없으면 내장 표기, 그것도 없으면
 * 코드를 그대로 보여 준다 — 빈칸으로 두면 자료가 사라진 것처럼 읽힌다.
 * (고객 코드값의 `resolveLabel` 과 같은 원칙)
 */
export const resolveTradeName = (
  rows: readonly { code: string; name: string }[],
  code: string
): string => {
  const row = rows.find((r) => r.code === code);
  if (row) return row.name;
  return isBuiltinTradeCode(code) ? TRADE_LABEL[code] : code;
};

/** 단위. 시드에 실제로 쓰인 값들 — 자유 입력도 허용하므로 제안 목록으로만 쓴다. */
export const UNIT_SUGGESTIONS = [
  "SET",
  "EA",
  "M",
  "PY",
  "T",
  "장",
  "실",
  "BOX",
] as const;
